import { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar } from 'lucide-react';
import { useDatePicker, type CalendarDay } from './useDatePicker';
import './DatePicker.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── Shared helpers ──────────────────────────────────────────────────────────

function formatDisplay(date: Date | null, placeholder = 'Select date'): string {
  if (!date) return placeholder;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDisplayDMY(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
}

function calculateAge(isoDate: string): number {
  const today = new Date();
  const birth = new Date(isoDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function dayClassNames(day: CalendarDay): string {
  const base = 'date-picker-v2__day';
  if (day.isDisabled) return `${base} date-picker-v2__day--disabled`;
  if (day.isRangeStart || day.isRangeEnd || day.isSelected) return `${base} date-picker-v2__day--range-edge`;
  if (day.isInRange) return `${base} date-picker-v2__day--in-range`;
  if (!day.isCurrentMonth) return `${base} date-picker-v2__day--outside`;
  return `${base} date-picker-v2__day--default`;
}

// ─── Shared calendar grid ─────────────────────────────────────────────────────

interface CalendarGridProps {
  currentYear: number;
  currentMonth: number;
  days: CalendarDay[];
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectDate: (date: Date) => void;
  /** When provided, renders month + year <select> elements in the nav. */
  navSelects?: {
    yearRange: { min: number; max: number };
    onYearSelect: (year: number) => void;
    onMonthSelect: (month: number) => void;
  };
}

function CalendarGrid({ currentYear, currentMonth, days, onPrevMonth, onNextMonth, onSelectDate, navSelects }: CalendarGridProps) {
  const years = navSelects
    ? Array.from({ length: navSelects.yearRange.max - navSelects.yearRange.min + 1 }, (_, i) => navSelects.yearRange.max - i)
    : null;

  return (
    <>
      <div className="date-picker-v2__nav">
        <button type="button" onClick={onPrevMonth} className="date-picker-v2__nav-btn" aria-label="Previous month">&lt;</button>
        <span className="date-picker-v2__month-label">
          {navSelects ? (
            <span className="date-picker-v2__nav-selects">
              <select
                className="date-picker-v2__nav-select"
                value={currentMonth}
                onChange={(e) => navSelects.onMonthSelect(Number(e.target.value))}
                aria-label="Month"
              >
                {MONTH_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}
              </select>
              <select
                className="date-picker-v2__nav-select"
                value={currentYear}
                onChange={(e) => navSelects.onYearSelect(Number(e.target.value))}
                aria-label="Year"
              >
                {years!.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </span>
          ) : (
            new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(currentYear, currentMonth))
          )}
        </span>
        <button type="button" onClick={onNextMonth} className="date-picker-v2__nav-btn" aria-label="Next month">&gt;</button>
      </div>

      <div className="date-picker-v2__weekdays">
        {WEEKDAYS.map((d) => <div key={d} className="date-picker-v2__weekday">{d}</div>)}
      </div>

      <div className="date-picker-v2__days">
        {days.map((day, i) => (
          <div key={i} className="date-picker-v2__day-cell">
            <button
              type="button"
              onClick={() => !day.isDisabled && onSelectDate(day.date)}
              className={dayClassNames(day)}
              aria-label={day.date.toDateString()}
              disabled={day.isDisabled}
            >
              {day.date.getDate()}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── DateRangePicker ──────────────────────────────────────────────────────────

export interface DateRangePickerProps {
  value?: { start: string | null; end: string | null };
  onChange?: (range: { start: string | null; end: string | null }) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  startPlaceholder?: string;
  endPlaceholder?: string;
  disabled?: boolean;
}

export const DateRangePicker = ({
  value, onChange, minDate, maxDate,
  startPlaceholder = 'Start',
  endPlaceholder = 'End',
  disabled = false,
}: DateRangePickerProps) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activePill, setActivePill] = useState<'start' | 'end' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { state, actions } = useDatePicker({ mode: 'range', value, onChange: disabled ? undefined : onChange, minDate, maxDate });

  const handlePillClick = (which: 'start' | 'end') => {
    if (disabled) return;

    // If currently closed, always open and set active pill
    if (!calendarOpen) {
      setCalendarOpen(true);
      setActivePill(which);
      return;
    }

    // If already open and clicking the same pill again -> close
    if (activePill === which) {
      setCalendarOpen(false);
      setActivePill(null);
      return;
    }

    // If open and clicking the other pill -> keep open, just switch active pill
    setActivePill(which);
  };

  return (
    <div
      className={`date-picker-v2 date-picker-v2--range${calendarOpen ? ' date-picker-v2--calendar-open' : ''}${disabled ? ' date-picker-v2--disabled' : ''}`}
      ref={containerRef}
    >
      <div className="date-picker-v2__pills">
        <button
          type="button"
          className="date-picker-v2__pill date-picker-v2__pill-btn"
          onClick={() => handlePillClick('start')}
          disabled={disabled}
          aria-expanded={calendarOpen && activePill === 'start'}
          aria-haspopup="dialog"
        >
          <div className="date-picker-v2__pill-label">{startPlaceholder}</div>
          <div className="date-picker-v2__pill-value">{formatDisplay(state.rangeStart, startPlaceholder)}</div>
        </button>
        <button
          type="button"
          className="date-picker-v2__pill date-picker-v2__pill-btn"
          onClick={() => handlePillClick('end')}
          disabled={disabled}
          aria-expanded={calendarOpen && activePill === 'end'}
          aria-haspopup="dialog"
        >
          <div className="date-picker-v2__pill-label">{endPlaceholder}</div>
          <div className="date-picker-v2__pill-value">{formatDisplay(state.rangeEnd, endPlaceholder)}</div>
        </button>
      </div>
      <div
        className={`date-picker-v2__calendar${calendarOpen && !disabled ? ' date-picker-v2__calendar--open' : ''}`}
        aria-hidden={!calendarOpen || disabled}
      >
        <CalendarGrid
          currentYear={state.currentYear}
          currentMonth={state.currentMonth}
          days={state.days}
          onPrevMonth={actions.prevMonth}
          onNextMonth={actions.nextMonth}
          onSelectDate={actions.selectDate}
        />
      </div>
    </div>
  );
};

// ─── SingleDatePicker ─────────────────────────────────────────────────────────

export interface SingleDatePickerProps {
  value?: string | null;
  onChange?: (date: string | null) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  /** Label shown in pill / trigger. Default: "Select date" */
  placeholder?: string;
  /** Formats value as DD.MM.YYYY (age years old). Enables month+year selects. */
  showAge?: boolean;
  /**
   * When true, renders an input-like trigger button that opens a dropdown
   * calendar (matches old DatePicker form UX). Default: false (inline calendar).
   */
  popover?: boolean;
  /** Where the popover calendar opens relative to the trigger. Default: bottom */
  popoverPlacement?: 'bottom' | 'top';
  /** Adds error border to the trigger. Only relevant when popover=true. */
  error?: boolean;
  /** Disables trigger and calendar interaction. */
  disabled?: boolean;
  /** Custom element to use as the trigger instead of the default button. */
  customTrigger?: React.ReactNode;
  /** Called when the trigger is clicked (before opening). */
  onTriggerClick?: () => void;
  /** Renders a hidden input with this id for label/form association. */
  id?: string;
  name?: string;
  autoComplete?: string;
}

export const SingleDatePicker = ({
  value, onChange, minDate, maxDate,
  placeholder = 'Select date',
  showAge = false,
  popover = false,
  popoverPlacement = 'bottom',
  error = false,
  disabled = false,
  customTrigger,
  onTriggerClick,
  id,
  name,
  autoComplete,
}: SingleDatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverPanelRef = useRef<HTMLDivElement>(null);

  // Close on outside click (only relevant in popover mode)
  useEffect(() => {
    if (!popover) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popover]);

  /** Mobile: scroll nearest scrollable ancestor so the calendar sits near the middle of the viewport. */
  useEffect(() => {
    if (!popover || !isOpen || disabled) return;
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 768px)').matches) return;

    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        popoverPanelRef.current?.scrollIntoView({
          block: 'center',
          inline: 'nearest',
          behavior: 'smooth',
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isOpen, popover, disabled]);

  // Wrap onChange to close popover on selection
  const handleChange = useCallback(
    (date: string | null) => {
      onChange?.(date);
      if (popover) setIsOpen(false);
    },
    [onChange, popover],
  );

  const { state, actions } = useDatePicker({
    mode: 'single',
    value,
    onChange: handleChange,
    minDate,
    maxDate,
  });

  const now = new Date();
  const navSelects = showAge
    ? {
        yearRange: {
          min: minDate ? minDate.getFullYear() : now.getFullYear() - 120,
          max: maxDate ? maxDate.getFullYear() : now.getFullYear(),
        },
        onYearSelect: actions.jumpToYear,
        onMonthSelect: actions.jumpToMonth,
      }
    : undefined;

  // Display text used in both pill (inline) and trigger (popover)
  const displayValue = (() => {
    if (!state.rangeStart) return placeholder;
    if (showAge && value) return `${formatDisplayDMY(state.rangeStart)} (${calculateAge(value)} years old)`;
    return formatDisplay(state.rangeStart);
  })();

  const calendarGrid = (
    <CalendarGrid
      currentYear={state.currentYear}
      currentMonth={state.currentMonth}
      days={state.days}
      onPrevMonth={actions.prevMonth}
      onNextMonth={actions.nextMonth}
      onSelectDate={actions.selectDate}
      navSelects={navSelects}
    />
  );

  // ── Popover mode ────────────────────────────────────────────────────────────
  if (popover) {
    return (
      <div style={{ position: 'relative', width: '100%' }} ref={containerRef}>
        {id && (
          <input
            type="text"
            id={id}
            name={name}
            autoComplete={autoComplete}
            value={value || ''}
            style={{ display: 'none' }}
            readOnly
            aria-hidden="true"
            tabIndex={-1}
          />
        )}

        {customTrigger ? (
          <div
            onClick={() => {
              if (disabled) return;
              setIsOpen((p) => !p);
              onTriggerClick?.();
            }}
            style={{ cursor: disabled ? 'default' : 'pointer' }}
          >
            {customTrigger}
          </div>
        ) : (
          <button
            type="button"
            style={{
              width: '100%',
              height: '3.5rem',
              background: '#F2F2F7',
              borderRadius: '0.75rem',
              border: error ? '1px solid #FF3B30' : '0',
              padding: '0 1rem',
              fontSize: '1rem',
              fontFamily: 'inherit',
              color: '#000',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
            onClick={(e) => {
              e.preventDefault();
              if (disabled) return;
              setIsOpen((p) => !p);
              onTriggerClick?.();
            }}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
          >
            <span style={{ color: value ? '#000' : '#8E8E93' }}>{displayValue}</span>
            <Calendar
              size={20}
              style={{
                flexShrink: 0,
                transition: 'all 0.3s',
                color: disabled ? '#8E8E93' : isOpen ? '#007AFF' : '#8E8E93',
                transform:
                  isOpen && !disabled
                    ? popoverPlacement === 'top'
                      ? 'none'
                      : 'rotate(180deg)'
                    : popoverPlacement === 'top'
                      ? 'rotate(180deg)'
                      : 'none',
              }}
            />
          </button>
        )}

        {isOpen && !disabled && (
          <div
            ref={popoverPanelRef}
            className={
              popoverPlacement === 'top'
                ? 'date-picker-v2__popover date-picker-v2__popover--placement-top'
                : 'date-picker-v2__popover'
            }
          >
            <div className="date-picker-v2">
              {calendarGrid}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Inline mode (default) ───────────────────────────────────────────────────
  return (
    <div className="date-picker-v2">
      <div className="date-picker-v2__pills">
        <div className="date-picker-v2__pill">
          <div className="date-picker-v2__pill-label">{placeholder}</div>
          <div className="date-picker-v2__pill-value">{displayValue}</div>
        </div>
      </div>
      {calendarGrid}
    </div>
  );
};

// Backwards-compatible alias: DatePicker = SingleDatePicker
export const DatePicker = SingleDatePicker;

// ─── Unified DatePickerProps type ─────────────────────────────────────────────

export type DatePickerProps =
  | ({ mode?: 'range' } & DateRangePickerProps)
  | ({ mode: 'single' } & SingleDatePickerProps);
